/* mile-card.js — product card enhancements (loaded from snippets/card-product.liquid).

   <mile-wishlist> — save without an account, localStorage only.
   Same element name and contract as the PDP implementation in
   assets/mile-product.js, registered here too because product cards render
   on pages where mile-product.js never loads (collection, search, homepage).
   Whichever file registers first wins — the markup contract is identical:

     <mile-wishlist data-handle="...">
       <button data-mile-wishlist-button aria-pressed="false"
               [data-label-add] [data-label-added]>
         ... [data-mile-wishlist-label] ...

   Storage: localStorage 'mile:wishlist' = JSON array of product handles.
   Change notifications: CustomEvent 'mile:wishlist:change' on document
   (same page) + native 'storage' event (other tabs). */

(() => {
  'use strict';

  const WISHLIST_KEY = 'mile:wishlist';

  const readWishlist = () => {
    try {
      const list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return [];
    }
  };

  const writeWishlist = (list) => {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
      return true;
    } catch (error) {
      // private mode / storage full — the button simply stays unsaved
      return false;
    }
  };

  if (!customElements.get('mile-wishlist')) {
    customElements.define(
      'mile-wishlist',
      class MileWishlist extends HTMLElement {
        connectedCallback() {
          this.handle = this.dataset.handle;
          this.button = this.querySelector('[data-mile-wishlist-button]');
          this.label = this.querySelector('[data-mile-wishlist-label]');
          if (!this.handle || !this.button) return;

          /* PREČO pointerdown A NIE click:
             Karta má na :hover transform (Dawn animate--hover-vertical-lift) a
             pod srdiečkom leží celoplošný odkaz na produkt (.card__heading a::after).
             Pri reálnom kliku myšou sa medzi mousedown a mouseup zmenil element pod
             kurzorom (mousedown na srdiečku, mouseup na odkaze), takže prehliadač
             vystrelil `click` až na spoločnom predkovi — handler tlačidla sa nikdy
             nespustil a srdiečko pôsobilo mŕtvo. pointerdown príde okamžite pri
             stlačení, kým sa layout nestihne pohnúť.

             click ostáva pre klávesnicu (Enter/Space negenerujú pointerdown);
             `handledAt` bráni dvojitému prepnutiu pri myši. */
          this.handledAt = 0;

          this.onPointerDown = (event) => {
            if (event.button != null && event.button !== 0) return; // len ľavé tlačidlo
            event.preventDefault();
            event.stopPropagation();
            this.handledAt = Date.now();
            this.toggle();
          };

          this.onClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (Date.now() - this.handledAt < 700) return; // už vybavil pointerdown
            this.toggle();
          };

          this.button.addEventListener('pointerdown', this.onPointerDown);
          this.button.addEventListener('click', this.onClick);

          this.onStorage = (event) => {
            if (event.key === WISHLIST_KEY) this.render();
          };
          window.addEventListener('storage', this.onStorage);

          /* Same-page sync: several cards (or card + PDP block) can show the
             same product — re-render everyone when anyone toggles. */
          this.onChange = () => this.render();
          document.addEventListener('mile:wishlist:change', this.onChange);

          this.render();
        }

        disconnectedCallback() {
          this.button?.removeEventListener('click', this.onClick);
          this.button?.removeEventListener('pointerdown', this.onPointerDown);
          window.removeEventListener('storage', this.onStorage);
          document.removeEventListener('mile:wishlist:change', this.onChange);
        }

        get saved() {
          return readWishlist().includes(this.handle);
        }

        toggle() {
          const list = readWishlist();
          const index = list.indexOf(this.handle);
          if (index === -1) {
            list.push(this.handle);
          } else {
            list.splice(index, 1);
          }
          if (!writeWishlist(list)) return;
          this.render();
          document.dispatchEvent(
            new CustomEvent('mile:wishlist:change', { detail: { handle: this.handle, list } })
          );
        }

        render() {
          const saved = this.saved;
          this.classList.toggle('is-saved', saved);
          this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
          if (this.label) {
            const text = saved ? this.button.dataset.labelAdded : this.button.dataset.labelAdd;
            if (text && this.label.textContent !== text) this.label.textContent = text;
          }
        }
      }
    );
  }
})();
